import { useState } from "react";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

export default function ShareModal({ open, onClose, title }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const url = typeof window !== "undefined" ? window.location.href : "";
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(`${title} - 影视工厂`);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard may be unavailable */
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const channels = [
    {
      label: "微博",
      icon: "ri-weibo-fill",
      href: `https://service.weibo.com/share/share.php?url=${encodedUrl}&title=${encodedTitle}`,
    },
    {
      label: "QQ空间",
      icon: "ri-qq-fill",
      href: `https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=${encodedUrl}&title=${encodedTitle}`,
    },
    {
      label: "豆瓣",
      icon: "ri-douban-fill",
      href: `https://www.douban.com/share/service?href=${encodedUrl}&name=${encodedTitle}`,
    },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-foreground-950/50" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-lg bg-background-50 p-6 animate-fade-up">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-medium text-foreground-950">分享给朋友</h3>
            <p className="mt-1 text-sm text-foreground-600 line-clamp-1">{title}</p>
          </div>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-background-100 flex items-center justify-center text-foreground-600 cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          {channels.map((channel) => (
            <a
              key={channel.label}
              href={channel.href}
              target="_blank"
              rel="noreferrer"
              className="flex flex-col items-center gap-2 py-3 rounded-lg bg-background-100 hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              <span className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center text-primary-600">
                <i className={`${channel.icon} text-lg`} />
              </span>
              <span className="text-xs text-foreground-700">{channel.label}</span>
            </a>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 p-2">
          <span className="flex-1 px-2 text-xs text-foreground-600 truncate">{url}</span>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            {copied ? "已复制" : "复制链接"}
          </button>
        </div>
      </div>
    </div>
  );
}